import { CountyProjectContext } from "@app/projects";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
    PciDashboardCounty,
    PciDashboardFiltersPayload,
    PciDashboardProcedure,
    PciDashboardProviderStatusEnum,
    PciDashboardScenarioTypeEnum,
    PciDashboardService,
    PciDashboardSpecialty,
    PciDashboardStatusEnum
} from "@app/projects/network-assessment/pci-dashboard/shared";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { groupBy, isEqual } from "lodash";
import React, { createContext, FC, useContext, useEffect, useState } from "react";

export interface PciDashboardFiltersContextValues {
  compareTimeslices: boolean;
  counties: PciDashboardCounty[];
  countyStatuses: DropdownItem<RegionSetStatusType>[];
  displayedFilters: PciDashboardFiltersPayload;
  filteredProcedures: DropdownItem<PciDashboardProcedure>[];
  filtersLoading: boolean;
  getFiltersPayload: () => PciDashboardFiltersPayload;
  newPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[];
  onClearFiltersClick: (showLineOfBusiness: boolean) => void;
  projectRunConfiguration: ProjectRunConfiguration;
  procedures: DropdownItem<PciDashboardProcedure>[];
  scenarioTypes: PciDashboardScenarioTypeEnum[];
  selectedCompareScenario: PciDashboardScenarioTypeEnum;
  selectedCounties: string[];
  selectedCountyStatuses: DropdownItem<RegionSetStatusType>[];
  selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
  selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[];
  selectedProcedures: DropdownItem<PciDashboardProcedure>[];
  selectedScenario: PciDashboardScenarioTypeEnum;
  selectedSpecialties: DropdownItem<PciDashboardSpecialty>[];
  serviceAreas: NestingParentDropdownItem[];
  setCompareTimeslices: (compareTimeslices: boolean) => void;
  setDisplayedFilters: (displayedFilters: PciDashboardFiltersPayload) => void;
  setFilteredProcedures: (filteredProcedures: DropdownItem<PciDashboardProcedure>[]) => void;
  setSelectedCompareScenario: (selectedCompareScenario: PciDashboardScenarioTypeEnum) => void;
  setSelectedCounties: (selectedCounties: string[]) => void;
  setSelectedCountyStatuses: (selectedCountyStatuses: DropdownItem<RegionSetStatusType>[]) => void;
  setSelectedProcedures: (selectedProcedures: DropdownItem<PciDashboardProcedure>[]) => void;
  setSelectedLineOfBusiness: (selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum) => void;
  setSelectedNewPatientClaimsFilters: (selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[]) => void;
  setSelectedSpecialties: (selectedSpecialties: DropdownItem<PciDashboardSpecialty>[]) => void;
  setSelectedScenario: (selectedScenario: PciDashboardScenarioTypeEnum) => void;
  specialties: DropdownItem<PciDashboardSpecialty>[];
}

const initialValues: PciDashboardFiltersContextValues = {
  compareTimeslices: false,
  counties: [],
  countyStatuses: [],
  displayedFilters: null,
  filteredProcedures: [],
  filtersLoading: false,
  getFiltersPayload: () => null,
  newPatientClaimsFilters: [],
  onClearFiltersClick: () => null,
  procedures: [],
  projectRunConfiguration: null,
  scenarioTypes: [],
  selectedCompareScenario: null,
  selectedCounties: [],
  selectedCountyStatuses: [],
  selectedLineOfBusiness: null,
  selectedNewPatientClaimsFilters: [],
  selectedProcedures: [],
  selectedScenario: null,
  selectedSpecialties: [],
  serviceAreas: [],
  setCompareTimeslices: () => null,
  setDisplayedFilters: () => null,
  setFilteredProcedures: () => null,
  setSelectedCompareScenario: () => null,
  setSelectedCounties: () => null,
  setSelectedCountyStatuses: () => null,
  setSelectedLineOfBusiness: () => null,
  setSelectedNewPatientClaimsFilters: () => null,
  setSelectedProcedures: () => null,
  setSelectedScenario: () => null,
  setSelectedSpecialties: () => null,
  specialties: [],
}

export const PciDashboardFiltersContext = createContext(initialValues);

const PciDashboardFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [compareTimeslices, setCompareTimeslices] = useState<boolean>(initialValues.compareTimeslices);
  const [counties, setCounties] = useState<PciDashboardCounty[]>(initialValues.counties);
  const [newPatientClaimsFilters, setNewPatientClaimsFilters] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);
  const [countyStatuses, setCountyStatuses] = useState<DropdownItem<RegionSetStatusType>[]>([]);
  const [displayedFilters, setDisplayedFilters] = useState<PciDashboardFiltersPayload>(initialValues.displayedFilters);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(initialValues.filtersLoading);
  const [projectRunConfiguration, setProjectRunConfiguration] = useState<ProjectRunConfiguration>(initialValues.projectRunConfiguration);
  const [scenarioTypes, setScenarioTypes] = useState<PciDashboardScenarioTypeEnum[]>([]);
  const [selectedCompareScenario, setSelectedCompareScenario] = useState<PciDashboardScenarioTypeEnum>(initialValues.selectedCompareScenario);
  const [selectedCounties, setSelectedCounties] = useState<string[]>(initialValues.selectedCounties);
  const [selectedCountyStatuses, setSelectedCountyStatuses] = useState<DropdownItem<RegionSetStatusType>[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(initialValues.selectedLineOfBusiness);
  const [selectedNewPatientClaimsFilters, setSelectedNewPatientClaimsFilters] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<PciDashboardScenarioTypeEnum>(initialValues.selectedScenario);
  const [selectedSpecialties, setSelectedSpecialties] = useState<DropdownItem<PciDashboardSpecialty>[]>(initialValues.selectedSpecialties);
  const [serviceAreas, setServiceAreas] = useState<NestingParentDropdownItem[]>(initialValues.serviceAreas);
  const [specialties, setSpecialties] = useState<DropdownItem<PciDashboardSpecialty>[]>(initialValues.specialties);
  const [procedures, setProcedures] = useState<DropdownItem<PciDashboardProcedure>[]>(initialValues.procedures);
  const [selectedProcedures, setSelectedProcedures] = useState<DropdownItem<PciDashboardProcedure>[]>([]);
  const [filteredProcedures, setFilteredProcedures] = useState<DropdownItem<PciDashboardProcedure>[]>([]);
  const [error, setError] = useState("");

  const pciDashboardService = new PciDashboardService();
  const projectContext = useContext(CountyProjectContext);

  useEffect(() => {
    if (!!projectContext?.project?.projectId &&
      !!projectRunConfiguration &&
      projectContext?.runConfiguration?.pciDashboardStatus === PciDashboardStatusEnum.PublishJobFinished
    ) {
      if (!!displayedFilters?.lineOfBusiness) {
        return;
      }

      setFiltersLoading(true);
      pciDashboardService.getPciFilterData(projectContext.project.projectId, projectRunConfiguration.projectRunConfigurationId).then((data) => {
        if (!isEqual(data.specialties.map(x => x.specialtyGroupCode), specialties.map(x => x.key.specialtyGroupCode))) {
          const mappedSpecialties: DropdownItem<PciDashboardSpecialty>[] = data.specialties.map(x => {
            return { key: x, name: `${x.specialtyGroupCode} - ${x.specialtyGroupName}` }
          });

          setSpecialties(mappedSpecialties);
          setSelectedSpecialties(mappedSpecialties);
        }

        if (!isEqual(data.procedures.map(x => x.code), procedures.map(x => x.key.code))) {
          const mappedProcedures: DropdownItem<PciDashboardProcedure>[] = data.procedures.map(x => {
            return { key: x, name: `${x.code} - ${x.description}` }
          });

          setProcedures(mappedProcedures);
        }

        if (!isEqual(data.countyStatuses, countyStatuses)) {
          const mappedCountyStatuses: DropdownItem<RegionSetStatusType>[] = data.countyStatuses.map(x => {
            return { key: x, name: x }
          });

          // on load, select all county statuses except opportunity
          setCountyStatuses(mappedCountyStatuses);
          setSelectedCountyStatuses(mappedCountyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity));
        }

        if (!isEqual(data.counties, counties)) {
          const groupedCountiesByState = groupBy(data.counties, x => x.stateName);
          const nestedServiceAreas: NestingParentDropdownItem[] = []

          for (const [key, value] of Object.entries(groupedCountiesByState)) {
            const children: DropdownItem<string>[] = value.map(x => {
              return { key: x.countySSA, name: x.countyName }
            });

            const nestedServiceArea: NestingParentDropdownItem = {
              checkboxStatus: CheckboxStateEnum.Checked,
              children,
              key,
              name: key,
            };

            nestedServiceAreas.push(nestedServiceArea)
          }

          setServiceAreas(nestedServiceAreas);
          setSelectedCounties(data.counties.map(x => x.countySSA));
          setCounties(data.counties);
        }

        if (!isEqual(data.scenarioTypes, scenarioTypes)) {
          setScenarioTypes(data.scenarioTypes);
        }

        if (!isEqual(data.newPatientFilters, newPatientClaimsFilters)) {
          const mappedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[] = data.newPatientFilters.map(x => {
            return { key: x, name: x }
          });

          setNewPatientClaimsFilters(mappedNewPatientClaimsFilters);
          setSelectedNewPatientClaimsFilters(mappedNewPatientClaimsFilters);
        }

        setFiltersLoading(false);
      }, (error) => {
        setFiltersLoading(false);
        setError(error);
      });
    }
  }, [projectContext?.project, projectRunConfiguration]);

  useEffect(() => {
    const runConfiguration: ProjectRunConfiguration = projectContext.project.projectRunConfigurations.find(x =>
      x && projectContext.runConfiguration && x.projectRunConfigurationType === projectContext.runConfiguration.projectRunConfigurationType &&
      x.isLatestPublished &&
      !x.isProjected &&
      !x.isTermed &&
      !x.isProjectedTermed &&
      x.projectRunConfigurationId !== projectRunConfiguration?.projectRunConfigurationId
    );

    if (!!runConfiguration) {
      setProjectRunConfiguration(runConfiguration);
    }
  }, [projectContext.runConfiguration, projectContext.project]);

  useEffect(() => {
    // once the specialties and service areas have been determined, set those as being the displayed filters
    if (!filtersLoading && !!projectRunConfiguration) {
      const filters: PciDashboardFiltersPayload = getFiltersPayload();
      setDisplayedFilters(filters);
    }
  }, [filtersLoading, projectRunConfiguration]);

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  const getFiltersPayload = (): PciDashboardFiltersPayload => {
    const filters: PciDashboardFiltersPayload = {
      claimsLabel: !!displayedFilters?.claimsLabel ? displayedFilters.claimsLabel : null,
      compareScenario: selectedCompareScenario,
      compareTimeslices: compareTimeslices,
      countySSAs: selectedCounties,
      countyStatuses: selectedCountyStatuses.map(x => x.key),
      insightsCategory: !!displayedFilters?.insightsCategory ? displayedFilters.insightsCategory : null,
      lineOfBusiness: !!selectedLineOfBusiness ? selectedLineOfBusiness : null,
      newPatientClaims: selectedNewPatientClaimsFilters.map(x => x.key),
      procedureCodes: selectedProcedures.map(x => x.key.code),
      projectRunConfigurationType: projectRunConfiguration.projectRunConfigurationType,
      providerNetworkType: !!displayedFilters?.providerNetworkType ? displayedFilters.providerNetworkType : null,
      providerStatus: PciDashboardProviderStatusEnum.Standard,
      scenario: selectedScenario,
      sortDirection: null,
      sortedColumn: null,
      specialtyGroupCodes: selectedSpecialties.map(x => x.key.specialtyGroupCode),
    }

    return filters;
  }

  const onClearFiltersClick = (showLineOfBusiness: boolean): void => {
    // if defaults are restored, all available counties and states should be displayed, selected
    setSelectedCounties(counties.map(x => x.countySSA));

    if (!!countyStatuses.length) {
      setSelectedCountyStatuses(countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity));
    }

    if (!!newPatientClaimsFilters.length) {
      setSelectedNewPatientClaimsFilters(newPatientClaimsFilters);
    }

    setSelectedSpecialties(specialties);
    setCompareTimeslices(false);
    setSelectedScenario(null);
    setSelectedCompareScenario(null);
    setSelectedProcedures([]);
    setFilteredProcedures([]);

    if (showLineOfBusiness) {
      setSelectedLineOfBusiness(ClaimsActivityLineOfBusinessTypeEnum.Total);
    }
  }

  return (
    <PciDashboardFiltersContext.Provider
      value={{
        compareTimeslices,
        counties,
        countyStatuses,
        displayedFilters,
        filteredProcedures,
        filtersLoading,
        getFiltersPayload,
        newPatientClaimsFilters,
        onClearFiltersClick,
        procedures,
        projectRunConfiguration,
        scenarioTypes,
        selectedCompareScenario,
        selectedCounties,
        selectedCountyStatuses,
        selectedLineOfBusiness,
        selectedNewPatientClaimsFilters,
        selectedProcedures,
        selectedScenario,
        selectedSpecialties,
        serviceAreas,
        setCompareTimeslices,
        setDisplayedFilters,
        setFilteredProcedures,
        setSelectedCompareScenario,
        setSelectedCounties,
        setSelectedCountyStatuses,
        setSelectedLineOfBusiness,
        setSelectedNewPatientClaimsFilters,
        setSelectedProcedures,
        setSelectedScenario,
        setSelectedSpecialties,
        specialties,
      }}>
      {props.children}
    </PciDashboardFiltersContext.Provider>
  )
}

export default PciDashboardFiltersContextProvider;